/**
 * รับรองเวลาเข้างาน (มุมพนักงาน) — ขอแก้/รับรองเวลาเข้า-ออกงาน กรณีลืมตอก/ตอกผิด/
 * เน็ตหลุด (เพราะปุ่มเช็คอิน-เช็คเอาท์ถูกล็อกกันกดเบิ้ล). อนุมัติโดย HR เท่านั้น.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, Paperclip, Send } from 'lucide-react';
import { resolveFileUrl, uploadFile } from '../lib/api-client';
import { PageLayout } from '../components/layout/PageLayout';
import { useMyAttendanceCorrections, useCreateAttendanceCorrection, type AttendanceCorrectionRow } from '../hooks';
import { useToast } from '../components/ui/toast';

const STATUS: Record<AttendanceCorrectionRow['status'], { label: string; cls: string }> = {
  pending_supervisor: { label: 'รอหัวหน้าอนุมัติ', cls: 'badge-warn' },
  pending: { label: 'รอ HR อนุมัติ', cls: 'badge-warn' },
  approved: { label: 'รับรองแล้ว', cls: 'badge-success' },
  rejected: { label: 'ไม่อนุมัติ', cls: 'badge-danger' },
  cancelled: { label: 'ยกเลิก', cls: 'badge-danger' },
};
const KIND_LABEL: Record<string, string> = { morning_in: 'เข้างาน (เช้า)', evening_out: 'เลิกงาน (เย็น)' };

function fmtThai(iso: string): string {
  const d = new Date(`${iso}T00:00:00+07:00`);
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(d);
}
function todayThai(): string {
  const nowThai = new Date(Date.now() + 7 * 3600_000);
  const t = new Date(Date.UTC(nowThai.getUTCFullYear(), nowThai.getUTCMonth(), nowThai.getUTCDate()));
  return t.toISOString().slice(0, 10);
}

export function AttendanceCorrection() {
  const mine = useMyAttendanceCorrections();
  const create = useCreateAttendanceCorrection();
  const toast = useToast();

  const maxDate = todayThai();   // รับรองย้อนหลังได้ ห้ามอนาคต
  // ลิงก์จากการ์ดไลน์ "ลืมสแกนออกงาน" / หน้ายืนยันเวลาเลิกงานที่ถูกปิด → ?date=YYYY-MM-DD&kind=evening_out
  const [params] = useSearchParams();
  const qDate = params.get('date');
  const [date, setDate] = useState(qDate && /^\d{4}-\d{2}-\d{2}$/.test(qDate) && qDate <= maxDate ? qDate : maxDate);
  const [kind, setKind] = useState<'morning_in' | 'evening_out'>(params.get('kind') === 'evening_out' ? 'evening_out' : 'morning_in');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');
  // fb#313 — หลักฐานแนบ (ไม่บังคับ) เช่น รูปจากแอปกล้องเวลา / แคปหน้าจอว่าโทรศัพท์ค้าง
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [uploading, setUploading] = useState(false);

  async function submit() {
    if (!date) return toast.error('เลือกวันที่');
    if (!time) return toast.error('ระบุเวลาที่ต้องการรับรอง');
    if (!reason.trim()) return toast.error('ต้องระบุเหตุผล');
    if (file && file.size > 5 * 1024 * 1024) return toast.error('ไฟล์หลักฐานใหญ่เกิน 5 MB — ย่อรูปหรือแคปใหม่');
    let evidence_path: string | undefined;
    if (file) {
      setUploading(true);
      try {
        evidence_path = await uploadFile(file, 'evidence', file.name);
      } catch (e) {
        setUploading(false);
        return toast.error(e instanceof Error ? `แนบหลักฐานไม่สำเร็จ: ${e.message}` : 'แนบหลักฐานไม่สำเร็จ');
      }
      setUploading(false);
    }
    create.mutate(
      { date, kind, requested_time: time, reason: reason.trim(), evidence_path },
      {
        onSuccess: () => {
          toast.success('ส่งคำขอรับรองเวลาแล้ว — รอ HR อนุมัติ');
          setTime(''); setReason(''); setFile(null); setFileKey((k) => k + 1);
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ส่งคำขอไม่สำเร็จ'),
      },
    );
  }
  const busy = uploading || create.isPending;

  const rows = mine.data ?? [];

  return (
    <PageLayout title="รับรองเวลาเข้า-ออกงาน">
      <div className="space-y-5 pb-10">
        <div className="card p-4 space-y-3">
          <div className="font-medium text-gray-900 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-teal" /> ขอรับรองเวลาเข้า-ออกงาน
          </div>
          <p className="text-xs text-gray-500">
            ใช้กรณี <b>ลืมตอก / ตอกผิดเวลา / เน็ตหลุด</b> — ระบุเวลาที่ถูกต้อง แล้วส่งให้ <b>HR</b> รับรอง.
            เมื่ออนุมัติ ระบบจะบันทึกเวลานี้เป็นเวลาเข้า/ออกงานของวันนั้น.
          </p>
          <div>
            <label className="label">วันที่</label>
            <input type="date" value={date} max={maxDate} onChange={(e) => setDate(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">รอบที่ขอรับรอง</label>
            <select value={kind} onChange={(e) => setKind(e.target.value as 'morning_in' | 'evening_out')} className="input w-full">
              <option value="morning_in">เข้างาน (เช้า)</option>
              <option value="evening_out">เลิกงาน (เย็น)</option>
            </select>
          </div>
          <div>
            <label className="label">เวลาที่ถูกต้อง</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">เหตุผล (จำเป็น)</label>
            <input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น ลืมตอกบัตรตอนเข้างาน" className="input w-full" maxLength={300}
            />
          </div>
          <div>
            <label className="label">แนบหลักฐาน (ถ้ามี)</label>
            <input
              key={fileKey}
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="input w-full text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">เช่น รูปจากแอปกล้องเวลา หรือแคปหน้าจอตอนโทรศัพท์ค้าง · รูปหรือ PDF ไม่เกิน 5 MB</p>
          </div>
          <button
            onClick={submit}
            disabled={busy || !date || !time || !reason.trim()}
            className="btn-cta w-full justify-center flex items-center gap-2"
          >
            <Send className="w-4 h-4" /> {uploading ? 'กำลังแนบหลักฐาน…' : create.isPending ? 'กำลังส่ง…' : 'ส่งคำขอรับรองเวลา'}
          </button>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">คำขอของฉัน</div>
          {mine.isLoading ? (
            <p className="text-sm text-gray-400">กำลังโหลด…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400">ยังไม่มีคำขอ</p>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="card p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-gray-900">
                    {fmtThai(r.date)} · {KIND_LABEL[r.kind] ?? r.kind} · <b>{r.requested_time} น.</b>
                  </div>
                  <span className={`${STATUS[r.status].cls} shrink-0`}>{STATUS[r.status].label}</span>
                </div>
                <p className="text-xs text-gray-500">เหตุผล: {r.reason}</p>
                {r.evidence_url && (
                  <a href={resolveFileUrl(r.evidence_url) ?? '#'} target="_blank" rel="noreferrer" className="text-xs text-brand-700 flex items-center gap-1 hover:underline">
                    <Paperclip className="w-3.5 h-3.5" /> หลักฐานที่แนบ
                  </a>
                )}
                {r.status === 'rejected' && r.rejection_reason && (
                  <p className="text-xs text-red-600">เหตุผลที่ไม่อนุมัติ: {r.rejection_reason}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </PageLayout>
  );
}
